/* Lightweight token + API client for the B2B ("entrepreneur") surface.
 *
 * Mirrors the client portal's approach: the B2B app lives in its own folder
 * and deliberately does NOT import the intranet's Redux store, RTK Query
 * slice, or auth feature. Session JWT lives in localStorage; b2bFetch wraps
 * fetch with the Authorization header.
 *
 * When the B2B surface eventually splits into its own deployable bundle this
 * file moves with the rest of `b2b/` unchanged.
 */

const TOKEN_STORAGE_KEY = 'pca-b2b-token';
// Marker set when a Prosperity admin is "logged in as" a B2B user, so the
// dashboard can show a banner + a way back. Holds { target_email }.
const IMPERSONATOR_STORAGE_KEY = 'pca-b2b-impersonator';

export function setB2BToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch { /* localStorage unavailable — silently no-op */ }
}

export function getB2BToken() {
  try { return window.localStorage.getItem(TOKEN_STORAGE_KEY); }
  catch { return null; }
}

export function clearB2BToken() {
  setB2BToken(null);
}

export function setB2BImpersonator(info) {
  try {
    if (info) window.localStorage.setItem(IMPERSONATOR_STORAGE_KEY, JSON.stringify(info));
    else window.localStorage.removeItem(IMPERSONATOR_STORAGE_KEY);
  } catch { /* localStorage unavailable — silently no-op */ }
}

export function getB2BImpersonator() {
  try {
    const raw = window.localStorage.getItem(IMPERSONATOR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearB2BImpersonator() {
  setB2BImpersonator(null);
}

/**
 * Authenticated fetch. Adds Authorization: Bearer <token> when a token is
 * stored, surfaces non-2xx as thrown errors with `.status` + `.detail`, and
 * JSON-parses responses (returns null for 204). On 401 clears the token — the
 * caller should redirect to /entrepreneur/login.
 */
export async function b2bFetch(path, options = {}) {
  const headers = new Headers(options.headers || {});
  const token = getB2BToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const isForm = options.body instanceof FormData;
  if (!isForm && options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(path, { ...options, headers });
  if (res.status === 401) {
    clearB2BToken();
    const err = new Error('Session expired. Please sign in again.');
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body?.detail || `Request failed (${res.status}).`);
    err.status = res.status;
    err.detail = body?.detail;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}
